import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { normalise, type Locale } from '@diana/shared'
import { api, type GlossaryEntry } from '../../lib/api.js'
import { useLocale } from '../../i18n/index.js'
import { speak, usePolishVoice } from '../../lib/speak.js'
import { Column, Main, TopTitle } from '../../classroom/Shell.js'
import { ButtonLink, ErrorLine, SearchBox, Skeleton, Tabs, Title } from '../../classroom/ui.js'
import { Icon } from '../../classroom/icons.js'
import { studentCopy } from './copy.js'
import { shellCopy } from './copyShell.js'

/** Search ignores case and Polish letters: „zawod” finds „zawód”. */
function fold(s: string): string {
  return normalise(s)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ł/g, 'l')
}

type Entry = GlossaryEntry & { courseSlug: string; courseLevel: string }

/**
 * Słownik (Student-Dictionary.png): the words of the lessons the student has
 * started, by lesson, searchable in Polish and in their own language, each
 * one tap away from the flashcards. The search box on Start lands here.
 */
export function GlossaryPage() {
  const locale = useLocale()
  const t = studentCopy(locale)
  const s = shellCopy(locale)
  const g = s.glossary
  const queryClient = useQueryClient()
  const voice = usePolishVoice()
  const [params, setParams] = useSearchParams()
  const [query, setQuery] = useState(params.get('q') ?? '')
  const [lesson, setLesson] = useState('all')

  const mine = useQuery({ queryKey: ['course', 'mine'], queryFn: api.course.mine })
  const courses = mine.data ?? []
  const details = useQueries({
    queries: courses.map((c) => ({ queryKey: ['course', c.slug], queryFn: () => api.course.detail(c.slug) })),
  })
  const glossaries = useQueries({
    queries: courses.map((c) => ({ queryKey: ['course', c.slug, 'glossary'], queryFn: () => api.course.glossary(c.slug) })),
  })
  const inReview = useQuery({ queryKey: ['review', 'vocab'], queryFn: api.review.vocabAdded })
  const add = useMutation({
    mutationFn: api.review.addWord,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review'] })
    },
  })

  // Only lessons the student has opened: a dictionary of what they have met.
  const courseSlug = params.get('kurs') ?? courses.find((_, i) => details[i]?.data?.lessons.some((l) => l.started))?.slug ?? courses[0]?.slug
  const entries: Entry[] = useMemo(() => {
    const out: Entry[] = []
    courses.forEach((c, i) => {
      const started = new Set(details[i]?.data?.lessons.filter((l) => l.started || l.completed).map((l) => l.number) ?? [])
      for (const e of glossaries[i]?.data ?? []) {
        if (started.has(e.lessonNumber)) out.push({ ...e, courseSlug: c.slug, courseLevel: c.level })
      }
    })
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses, details.map((d) => d.dataUpdatedAt).join(), glossaries.map((q) => q.dataUpdatedAt).join()])

  const q = fold(query.trim())
  const inCourse = entries.filter((e) => e.courseSlug === courseSlug)
  const lessonsHere = Array.from(new Set(inCourse.map((e) => e.lessonNumber))).sort((a, b) => b - a)
  const shown = q
    ? entries.filter((e) => fold(e.term).includes(q) || fold(e.uk).includes(q) || fold(e.ru).includes(q))
    : inCourse.filter((e) => lesson === 'all' || String(e.lessonNumber) === lesson).sort((a, b) => b.lessonNumber - a.lessonNumber)
  const added = new Set(inReview.data ?? [])
  const addedHere = shown.filter((e) => added.has(e.id)).length

  const loading = mine.isPending || details.some((d) => d.isPending) || glossaries.some((q) => q.isPending)
  const gloss = (e: Entry, l: Locale) => (l === 'ru' ? e.ru : e.uk)

  return (
    <Column
      topOnPhone
      top={
        <>
          <TopTitle>{s.nav.glossary}</TopTitle>
          <SearchBox
            className="ml-auto"
            width={280}
            value={query}
            onChange={(v) => {
              setQuery(v)
              setParams(
                (p) => {
                  const n = new URLSearchParams(p)
                  if (v.trim()) n.set('q', v.trim())
                  else n.delete('q')
                  return n
                },
                { replace: true },
              )
            }}
            placeholder={`${g.search} (PL / ${locale === 'ru' ? 'RU' : 'UA'})`}
            label={g.search}
          />
        </>
      }
      bottom={
        shown.length > 0 && (
          <>
            <span className="hidden text-class-gloss text-class-ink-soft desk:inline">{g.inList(addedHere)}</span>
            <ButtonLink to="/panel/powtorki" className="ml-auto max-desk:w-full">
              {g.review}
            </ButtonLink>
          </>
        )
      }
    >
      <Main width={720} className="desk:!gap-[52px]">
        <Title sub={g.sub}>{s.nav.glossary}</Title>
        {loading ? (
          <Skeleton lines={6} />
        ) : mine.isError ? (
          <ErrorLine>{t.errors.load}</ErrorLine>
        ) : entries.length === 0 ? (
          <div>
            <p className="m-0 font-semibold">{g.none}</p>
            <p className="m-0 text-class-gloss text-class-ink-faint">{g.noneHint}</p>
          </div>
        ) : (
          <section className="flex flex-col">
            {!q && courses.filter((c) => entries.some((e) => e.courseSlug === c.slug)).length > 1 && (
              <div className="mb-4">
                <Tabs
                  label="Kurs"
                  value={courseSlug ?? ''}
                  onChange={(v) => {
                    setLesson('all')
                    setParams((p) => {
                      const n = new URLSearchParams(p)
                      n.set('kurs', v)
                      return n
                    })
                  }}
                  items={courses.filter((c) => entries.some((e) => e.courseSlug === c.slug)).map((c) => ({ value: c.slug, label: c.title }))}
                />
              </div>
            )}
            {!q && (
              <div className="mb-4 overflow-x-auto">
                <Tabs
                  label="Lekcja"
                  value={lesson}
                  onChange={setLesson}
                  items={[{ value: 'all', label: g.all }, ...lessonsHere.map((n) => ({ value: String(n), label: g.lesson(n) }))]}
                />
              </div>
            )}
            {shown.length === 0 ? (
              <p className="m-0 text-class-ink-soft">{g.nothingFound(query.trim())}</p>
            ) : (
              <div className="flex flex-col [&>*]:border-t [&>*]:border-class-line [&>*:last-child]:border-b">
                {shown.slice(0, 400).map((e) => (
                  <div key={`${e.courseSlug}:${e.id}`} className="flex min-h-16 items-center gap-4 px-1 py-1.5">
                    {voice ? (
                      <button
                        type="button"
                        aria-label={`${g.listen}: ${e.term}`}
                        onClick={() => speak(e.term)}
                        className="inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-class-field-soft text-class-sky-700"
                      >
                        <Icon name="speaker" size={18} />
                      </button>
                    ) : (
                      <span className="w-9 shrink-0" />
                    )}
                    <span className="min-w-0 flex-1">
                      <span lang="pl" className="block font-class-display text-class-subheading leading-snug">
                        {e.term}
                      </span>
                      <span className="block text-class-gloss text-class-ink-faint">{gloss(e, locale) || e.uk}</span>
                    </span>
                    <span className="hidden w-24 text-class-label leading-tight text-class-ink-faint desk:block">
                      {q && courses.length > 1 && <span className="block">{e.courseLevel}</span>}
                      {g.lesson(e.lessonNumber)}
                    </span>
                    <span className="w-[120px] text-right">
                      {added.has(e.id) ? (
                        <span className="text-class-label font-semibold text-class-ink-faint">{g.inReview}</span>
                      ) : (
                        <button
                          type="button"
                          disabled={add.isPending}
                          onClick={() => add.mutate(e.id)}
                          className="cl-link text-class-label"
                        >
                          {g.add}
                        </button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </Main>
    </Column>
  )
}
